// Transient outbound-fetch failures from Vercel Lambda → Fly.io backends
// (connect timeouts, socket resets, FetchError wrapper from the generated
// OpenAPI clients). These are upstream-blip class — not user-actionable.
// We tag them in Sentry under a single fingerprint so they group cleanly
// for alerting, and we suppress the user-facing toast/alert.

const FETCH_ERROR_MARKER =
  "The request failed and the interceptors did not return an alternative response";

// Exact native-fetch failure messages — must match by equality, NOT substring.
// App code routinely throws "Failed to fetch <thing>" strings (e.g.
// PaymentSelection: `handleError("Failed to fetch payment methods", ...)`)
// and a substring match would silently bucket those under the transient
// fingerprint and hide them from normal alerting.
const TRANSIENT_NETWORK_ERROR_MESSAGES = new Set([
  "Failed to fetch", // Chrome / Edge / undici browser fetch
  "Load failed", // Safari
  "Network request failed", // whatwg-fetch / RN
  "NetworkError when attempting to fetch resource.", // Firefox (with trailing period)
]);

const TRANSIENT_OUTBOUND_CAUSE_CODES = new Set([
  "UND_ERR_CONNECT_TIMEOUT",
  "UND_ERR_SOCKET",
  "ECONNRESET",
  "ETIMEDOUT",
  "EAI_AGAIN",
]);

const messageMatchesNetworkPattern = (message: unknown): boolean =>
  typeof message === "string" && TRANSIENT_NETWORK_ERROR_MESSAGES.has(message);

export const isTransientOutboundError = (error: unknown): boolean => {
  if (error == null || typeof error !== "object") return false;
  const e = error as Error & { cause?: unknown };

  if (e.name === "FetchError") return true;
  // Exact match — the marker is the FetchError default message at
  // generated/*/runtime.ts:221. App code never writes this verbatim.
  if (e.message === FETCH_ERROR_MARKER) return true;
  if (messageMatchesNetworkPattern(e.message)) return true;

  if (e.cause && typeof e.cause === "object") {
    const cause = e.cause as { code?: unknown; message?: unknown };
    if (
      typeof cause.code === "string" &&
      TRANSIENT_OUTBOUND_CAUSE_CODES.has(cause.code)
    )
      return true;
    if (messageMatchesNetworkPattern(cause.message)) return true;
  }
  return false;
};

export const TRANSIENT_OUTBOUND_FINGERPRINT = ["outbound-transient-fetch"];
export const TRANSIENT_OUTBOUND_TAG_KEY = "outbound_fetch_error";
